import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["pill", "notice"];
  static values = {
    subscriptionId: Number,
    limit: Number
  };

  declare subscriptionIdValue: number;
  declare limitValue: number | null;
  declare pillTarget: HTMLElement;
  declare noticeTarget: HTMLElement | null;

  private boundHandleCheckboxChange: (event: Event) => void;

  connect() {
    this.boundHandleCheckboxChange = this.handleCheckboxChange.bind(this);
    document.addEventListener("change", this.boundHandleCheckboxChange);
    this.updateDisplay();
    this.updateCheckboxStates();
  }

  disconnect() {
    document.removeEventListener("change", this.boundHandleCheckboxChange);
  }

  private handleCheckboxChange(event: Event) {
    const target = event.target as HTMLElement;
    if (target.classList.contains("permission-matrix-checkbox")) {
      const checkbox = target as HTMLInputElement;
      const subscriptionId = checkbox.dataset.permissionMatrixSubscriptionIdParam;
      if (subscriptionId && parseInt(subscriptionId) === this.subscriptionIdValue) {
        this.updateDisplay();
        this.updateCheckboxStates();
      }
    }
  }

  private getCheckboxes(): HTMLInputElement[] {
    return Array.from(
      document.querySelectorAll<HTMLInputElement>(
        `.permission-matrix-checkbox[data-permission-matrix-subscription-id-param="${this.subscriptionIdValue}"]`
      )
    );
  }

  private getCurrentUsed(): number {
    return this.getCheckboxes().filter(cb => cb.checked).length;
  }

  private isUnlimited(): boolean {
    const attr = this.element.getAttribute("data-permission-matrix-limit-value");
    return attr === null || attr === "";
  }

  private updateDisplay() {
    const used = this.getCurrentUsed();
    const limit = this.limitValue;

    if (this.isUnlimited()) {
      this.pillTarget.textContent = "Unbegrenzt";
      this.updatePillColor("bg-slate-100", "text-slate-700");
      if (this.noticeTarget) {
        this.noticeTarget.style.display = "none";
      }
      return;
    }

    this.pillTarget.textContent = `${used} von ${limit} Zugriffen`;

    const exactlyFull = used === limit;
    const overLimit = used > limit;

    if (overLimit) {
      this.updatePillColor("bg-rose-100", "text-rose-700");
      if (this.noticeTarget) {
        this.noticeTarget.style.display = "flex";
        this.updateNoticeColor("text-rose-700");
      }
    } else if (exactlyFull) {
      this.updatePillColor("bg-yellow-100", "text-yellow-700");
      if (this.noticeTarget) {
        this.noticeTarget.style.display = "flex";
        this.updateNoticeColor("text-yellow-700");
      }
    } else {
      this.updatePillColor("bg-slate-100", "text-slate-700");
      if (this.noticeTarget) {
        this.noticeTarget.style.display = "none";
      }
    }
  }

  private updatePillColor(bgClass: string, textClass: string) {
    this.pillTarget.className = `inline-flex items-center justify-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${bgClass} ${textClass}`;
  }

  private updateNoticeColor(textClass: string) {
    if (this.noticeTarget) {
      const classes = this.noticeTarget.className.split(" ");
      const filtered = classes.filter(c => !c.startsWith("text-"));
      this.noticeTarget.className = [...filtered, textClass].join(" ");
    }
  }

  private updateCheckboxStates() {
    const used = this.getCurrentUsed();
    const limit = this.limitValue;
    const checkboxes = this.getCheckboxes();

    if (this.isUnlimited()) {
      checkboxes.forEach(cb => {
        cb.disabled = false;
      });
      return;
    }

    checkboxes.forEach(cb => {
      if (cb.checked) {
        cb.disabled = false;
      } else {
        cb.disabled = used >= limit;
      }
    });
  }
}
